import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  InputNumber,
  Slider,
  Space,
  Switch,
  Tooltip,
  Typography,
  message,
} from 'antd';
import React, { useState } from 'react';
import * as THREE from 'three';
import { SnappingService } from '../../../../services/SnappingService';

const { Title, Text } = Typography;

interface SpatialGridCell {
  bounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
  };
  objects: any[];
}

interface PerformanceMetrics {
  fps: number;
  objectCount: number;
  drawCalls: number;
  triangles: number;
  lastFrameTime: number;
}

interface PerformanceDebugPanelProps {
  snappingService: SnappingService;
  scene: React.MutableRefObject<THREE.Scene | null>;
  renderer: React.MutableRefObject<THREE.WebGLRenderer | null>;

  // Spatial partitioning
  isSpatialGridEnabled: boolean;
  setIsSpatialGridEnabled: (enabled: boolean) => void;
  spatialGridCellSize: number;
  setSpatialGridCellSize: (size: number) => void;

  // Level of detail
  isLoDEnabled: boolean;
  setIsLoDEnabled: (enabled: boolean) => void;

  // Frustum culling
  isFrustumCullingEnabled: boolean;
  setIsFrustumCullingEnabled: (enabled: boolean) => void;

  // Max objects
  maxObjectsToCheck: number;
  setMaxObjectsToCheck: (count: number) => void;

  // Debug visualization
  showDebugVisualization: boolean;
  setShowDebugVisualization: (show: boolean) => void;
}

export const PerformanceDebugPanel: React.FC<PerformanceDebugPanelProps> = ({
  snappingService,
  scene,
  renderer,
  isSpatialGridEnabled,
  setIsSpatialGridEnabled,
  spatialGridCellSize,
  setSpatialGridCellSize,
  isLoDEnabled,
  setIsLoDEnabled,
  isFrustumCullingEnabled,
  setIsFrustumCullingEnabled,
  maxObjectsToCheck,
  setMaxObjectsToCheck,
  showDebugVisualization,
  setShowDebugVisualization,
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 0,
    objectCount: 0,
    drawCalls: 0,
    triangles: 0,
    lastFrameTime: 0,
  });

  const [isMonitoring, setIsMonitoring] = useState(false);

  // Add dragging state
  const [position, setPosition] = useState({ x: 10, y: 10 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Handle enabling/disabling spatial grid
  const handleSpatialGridToggle = (checked: boolean) => {
    setIsSpatialGridEnabled(checked);
    snappingService.setSpatialGridEnabled(checked);
    message.success(`Spatial Grid ${checked ? 'enabled' : 'disabled'}`);
  };

  // Handle changing spatial grid cell size
  const handleCellSizeChange = (value: number | null) => {
    if (value !== null) {
      setSpatialGridCellSize(value);
      snappingService.setSpatialGridCellSize(value);
      message.success('Spatial Grid cell size updated');
    }
  };

  // Handle enabling/disabling LoD
  const handleLoDToggle = (checked: boolean) => {
    setIsLoDEnabled(checked);
    snappingService.setLoDEnabled(checked);
    message.success(`Level of Detail ${checked ? 'enabled' : 'disabled'}`);
  };

  // Handle enabling/disabling frustum culling
  const handleFrustumCullingToggle = (checked: boolean) => {
    setIsFrustumCullingEnabled(checked);
    snappingService.setFrustumCullingEnabled(checked);
    message.success(`Frustum Culling ${checked ? 'enabled' : 'disabled'}`);
  };

  // Handle changing max objects to check
  const handleMaxObjectsChange = (value: number | null) => {
    if (value !== null) {
      setMaxObjectsToCheck(value);
      snappingService.setMaxObjectsToCheck(value);
      message.success('Max Objects updated');
    }
  };

  // Toggle debug visualization
  const handleDebugVisualizationToggle = (checked: boolean) => {
    setShowDebugVisualization(checked);

    // Show or hide spatial grid visualization
    if (scene.current) {
      // Remove any existing debug visualizations
      const existingVisualizations = scene.current.children.filter(
        (child) => child.userData.isSpatialGridVisualization
      );

      existingVisualizations.forEach((vis) => {
        scene.current!.remove(vis);
      });

      // Create new visualizations if enabled
      if (checked && isSpatialGridEnabled) {
        visualizeSpatialGrid();
      }
    }
  };

  // Update spatial grid and visualize
  const updateSpatialGrid = () => {
    if (scene.current) {
      snappingService.updateSpatialGrid(scene.current);

      if (showDebugVisualization) {
        visualizeSpatialGrid();
      }
    }
  };

  // Visualize the spatial grid
  const visualizeSpatialGrid = () => {
    if (!scene.current) return;

    // Remove any existing visualizations
    const existingVisualizations = scene.current.children.filter(
      (child) => child.userData.isSpatialGridVisualization
    );

    existingVisualizations.forEach((vis) => {
      scene.current!.remove(vis);
    });

    // Access private _spatialGrid from snappingService
    // This is a bit hacky, but it's for debug purposes only
    const grid = (snappingService as any)._spatialGrid;

    if (!grid) return;

    // Create a visualization for each cell
    grid.cells.forEach((cell: SpatialGridCell) => {
      const { minX, maxX, minZ, maxZ } = cell.bounds;
      const width = maxX - minX;
      const depth = maxZ - minZ;

      // Create a thin box to represent the cell
      const geometry = new THREE.BoxGeometry(width, 0.01, depth);
      const material = new THREE.MeshBasicMaterial({
        color: cell.objects.length > 0 ? 0xff0000 : 0x00ff00,
        transparent: true,
        opacity: 0.2,
        wireframe: true,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(
        minX + width / 2,
        0.01, // Just above the floor
        minZ + depth / 2
      );

      mesh.userData.isSpatialGridVisualization = true;
      scene.current!.add(mesh);
    });
  };

  // Start/stop performance monitoring
  const toggleMonitoring = () => {
    setIsMonitoring(!isMonitoring);

    if (!isMonitoring) {
      // Start monitoring
      let lastTime = performance.now();
      let frames = 0;

      const updateMetrics = () => {
        if (!isMonitoring) return;

        frames++;
        const now = performance.now();

        // Update once per second
        if (now >= lastTime + 1000) {
          const fps = Math.round((frames * 1000) / (now - lastTime));

          // Get scene stats
          let objectCount = 0;
          let triangles = 0;

          if (scene.current) {
            objectCount = scene.current.children.length;

            scene.current.traverse((object) => {
              if ((object as THREE.Mesh).isMesh) {
                const mesh = object as THREE.Mesh;
                if (mesh.geometry) {
                  if (mesh.geometry.index !== null) {
                    triangles += mesh.geometry.index.count / 3;
                  } else if (mesh.geometry.attributes.position) {
                    triangles += mesh.geometry.attributes.position.count / 3;
                  }
                }
              }
            });
          }

          // Get renderer stats if available
          let drawCalls = 0;
          if (renderer.current && (renderer.current as any).info) {
            drawCalls = (renderer.current as any).info.render.calls;
          }

          setMetrics({
            fps,
            objectCount,
            drawCalls,
            triangles: Math.round(triangles),
            lastFrameTime: (now - lastTime) / frames,
          });

          lastTime = now;
          frames = 0;
        }

        requestAnimationFrame(updateMetrics);
      };

      updateMetrics();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.ant-card-head')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Keep panel within window bounds
      const maxX = window.innerWidth - 320; // panel width + margin
      const maxY = window.innerHeight - 100; // approximate height

      setPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Card
      title="Performance Optimization"
      size="small"
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: 300,
        zIndex: 1000,
        background: '#fff',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      onMouseDown={handleMouseDown}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={5}>
          Spatial Partitioning
          <Tooltip title="Spatial partitioning splits the game world into smaller boxes. This helps find things faster, like when you need to check if objects are touching. Think of it like sorting your toys into different boxes - it's easier to find a specific toy when you know which box to look in!">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </Title>
        <Space>
          <Switch
            checked={isSpatialGridEnabled}
            onChange={handleSpatialGridToggle}
          />
          <Text>Enable Spatial Grid</Text>
        </Space>

        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Cell Size: {spatialGridCellSize}
            <Tooltip title="This sets how big each box in the grid is. Bigger boxes mean fewer boxes but more things in each box. Smaller boxes mean more boxes but fewer things in each box. Like choosing between having a few big toy boxes or many small ones.">
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </Text>
          <Slider
            min={1}
            max={20}
            step={0.5}
            value={spatialGridCellSize}
            onChange={handleCellSizeChange}
            disabled={!isSpatialGridEnabled}
          />
        </Space>

        <Space>
          <Button
            size="small"
            onClick={updateSpatialGrid}
            disabled={!isSpatialGridEnabled}
          >
            Update Grid
          </Button>
          <Tooltip title="This rebuilds all the boxes and puts everything in its right place. Like re-organizing your toys into boxes after you've moved them around.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <Title level={5}>
          Object Culling
          <Tooltip title="Culling means not showing things you can't see. It's like not worrying about toys that are in another room - you can't see them anyway!">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Checkbox
              checked={isLoDEnabled}
              onChange={(e) => handleLoDToggle(e.target.checked)}
            />
            <Text>Level of Detail</Text>
            <Tooltip title="This makes far-away things simpler to draw. Like how a far-away house looks like a simple box - you don't need to see every window and brick when it's tiny on your screen.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>

          <Space>
            <Checkbox
              checked={isFrustumCullingEnabled}
              onChange={(e) => handleFrustumCullingToggle(e.target.checked)}
            />
            <Text>Frustum Culling</Text>
            <Tooltip title="This only draws what's in front of the camera. Like how you can only see what's in front of your eyes - there's no need to draw things behind you!">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>

          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              Max Objects: {maxObjectsToCheck}
              <Tooltip title="This limits how many things we check at once. Like if you have 100 toys but only check the closest 20 when looking for a specific one. It makes things faster but might miss some far away toys.">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Text>
            <InputNumber
              min={10}
              max={1000}
              step={10}
              value={maxObjectsToCheck}
              onChange={handleMaxObjectsChange}
              style={{ width: '100%' }}
            />
          </Space>
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <Title level={5}>
          Visualization
          <Tooltip title="This shows you how the world is split into boxes. The green boxes are empty, and the red boxes have things in them. It's like seeing through the walls of your toy boxes!">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </Title>
        <Space>
          <Switch
            checked={showDebugVisualization}
            onChange={handleDebugVisualizationToggle}
          />
          <Text>Show Debug Visualization</Text>
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <Title level={5}>
          Performance Metrics
          <Tooltip title="These numbers tell you how well everything is running. FPS is how smooth it looks (higher is better), and the other numbers show how much work your computer is doing to show everything.">
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </Title>
        <Button
          size="small"
          type={isMonitoring ? 'primary' : 'default'}
          onClick={toggleMonitoring}
        >
          {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
        </Button>

        {isMonitoring && (
          <div style={{ marginTop: 8 }}>
            <Text>FPS: {metrics.fps}</Text>
            <br />
            <Text>Objects: {metrics.objectCount}</Text>
            <br />
            <Text>Draw Calls: {metrics.drawCalls}</Text>
            <br />
            <Text>Triangles: {metrics.triangles}</Text>
            <br />
            <Text>Frame Time: {metrics.lastFrameTime.toFixed(2)}ms</Text>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default PerformanceDebugPanel;
